import React, { useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnType, TableProps } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';

interface DataTableProps<T> {
  columns: CustomColumnType<T>[];
  dataSource: T[];
  inactiveFlag?: keyof T;
  pagination?: TableProps<T>['pagination'];
  onChange?: TableProps<T>['onChange'];
  rowKey?: TableProps<T>['rowKey'];
}

interface CustomColumnType<T> extends TableColumnType<T> {
  searchable?: boolean;
}

const DataTable = <T extends object>({
  columns,
  dataSource,
  inactiveFlag,
  pagination,
  onChange,
  rowKey,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState<keyof T | ''>('');
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm'], dataIndex: keyof T) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: keyof T): TableColumnType<T> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()} key={Math.random()}>
        <Input
          ref={searchInput}
          placeholder={t('Buscar {{field}}', { field: String(dataIndex) })}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            {t('Buscar')}
          </Button>
          <Button onClick={() => clearFilters && handleReset(clearFilters)} size='small' style={{ width: 90 }}>
            {t('Limpiar')}
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            {t('Filtrar')}
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              close();
            }}
          >
            {t('Cerrar')}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) =>
      String(record[dataIndex])
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? String(text) : ''}
        />
      ) : (
        text
      ),
  });

  const enhancedColumns = columns.map((col) => {
    const searchProps = col.searchable ? getColumnSearchProps(col.dataIndex as keyof T) : {};
    return {
      ...col,
      ...searchProps,
      render: col.render
        ? (value: any, record: T, index: number) => (
            <>
              {searchedColumn === col.dataIndex ? (
                <Highlighter
                  highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                  searchWords={[searchText]}
                  autoEscape
                  textToHighlight={value ? String(value) : ''}
                />
              ) : (
                col.render!(value, record, index)
              )}
            </>
          )
        : searchProps.render,
    };
  });

  const resolvedRowKey: TableProps<T>['rowKey'] =
    rowKey ??
    ((record: T) => {
      const typedRecord = record as unknown as { id?: React.Key; key?: React.Key };
      if (typedRecord?.id !== undefined && typedRecord?.id !== null) {
        return typedRecord.id;
      }
      if (typedRecord?.key !== undefined && typedRecord?.key !== null) {
        return typedRecord.key;
      }
      return JSON.stringify(record);
    });

  return (
    <Table
      columns={enhancedColumns}
      dataSource={dataSource}
      pagination={pagination}
      onChange={onChange}
      rowKey={resolvedRowKey}
      rowClassName={(record) => (inactiveFlag && record[inactiveFlag] === false ? 'row-inactive' : '')}
    />
  );
};

export default DataTable;
